import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Trash2, Loader2, Users, User } from "lucide-react";
import SectionInfoBanner from "@/components/SectionInfoBanner";

export default function DipendentiBasePage() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      // Hide self (titolare) from the deletable list — we only show employees
      setUsers(res.data.filter(u => u.id !== user?.id));
    } catch (e) {
      toast.error("Errore nel caricamento dei dipendenti");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${confirmTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${confirmTarget.nome} ${confirmTarget.cognome} eliminato`);
      setConfirmTarget(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const ruoloLabel = (r) => {
    if (r === "titolare") return "Titolare";
    if (r === "amministrativo") return "Amministrativo";
    return "Dipendente";
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
        <SectionInfoBanner
          sectionKey="elenco-dipendenti"
          title="Cosa puoi fare in Elenco dipendenti"
          items={[
            "Vedi tutti i dipendenti che si sono registrati con il tuo codice azienda",
            "Quando un dipendente lascia l'azienda, premi il cestino rosso per eliminarlo",
            "L'app ti chiede sempre conferma prima di eliminare (puoi annullare con 'No')",
            "Eliminando un dipendente liberi un posto sul tuo piano (Base = fino a 5 dipendenti)",
          ]}
        />
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="dipendenti-title">
            Elenco dipendenti
          </h1>
          <p className="text-slate-500 mt-1">
            Visualizza i dipendenti che si sono registrati con il codice della tua azienda
          </p>
        </div>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" style={{ color: "var(--theme-600)" }} />
              {users.length} {users.length === 1 ? "dipendente registrato" : "dipendenti registrati"}
            </CardTitle>
            <CardDescription>
              Quando un dipendente lascia l'azienda, eliminalo da qui per liberare il posto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Nessun dipendente registrato finora.</p>
                <p className="text-xs mt-2">Condividi il codice azienda dalla Home per invitarli.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {users.map((u) => (
                  <li
                    key={u.id}
                    className="flex items-center gap-3 py-3 sm:py-4"
                    data-testid={`dipendente-row-${u.id}`}
                  >
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--theme-100)" }}
                    >
                      <User className="h-5 w-5" style={{ color: "var(--theme-600)" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {u.nome} {u.cognome}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="hidden sm:inline-flex shrink-0">
                      {ruoloLabel(u.ruolo)}
                    </Badge>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                            onClick={() => setConfirmTarget(u)}
                            data-testid={`delete-dipendente-btn-${u.id}`}
                            aria-label="Elimina dipendente"
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          Il dipendente non c'è più: eliminalo!
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi cancellare <strong>{confirmTarget?.nome} {confirmTarget?.cognome}</strong>?
              <br />
              Questa azione eliminerà anche tutte le richieste e le notifiche del dipendente. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="confirm-delete-no">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-delete-yes"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sì, elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
